import type { CatalogProductImage } from "@shared/types";
import { PRODUCT_TIERS, PRODUCT_UOMS } from "@shared/types";
import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { Skeleton } from "@shared-ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared-ui/tabs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDirtyState } from "@/components/ui/entity-form";
import { notify } from "@/components/ui/notify";
import { StatusBadge } from "@/components/ui/status-badge";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { CategoryPicker } from "@/features/catalog/components/category-picker";
import { DuplicateProductDialog } from "@/features/catalog/components/duplicate-product-dialog";
import { ProductMedia } from "@/features/catalog/components/product-media";
import { ProductPricing } from "@/features/catalog/components/product-pricing";
import { ProductProvenance } from "@/features/catalog/components/product-provenance";
import {
	isFilled,
	SpecAttributeField,
	type SpecValue,
} from "@/features/catalog/components/spec-attribute-field";
import { VariantList } from "@/features/catalog/components/variant-list";
import { uomLabel } from "@/features/catalog/lib/format";
import { sameImages } from "@/features/catalog/lib/images";
import { useEditorExitGuard } from "@/features/catalog/lib/use-editor-exit-guard";
import type { AdminProductDetail } from "@/features/catalog/services/catalog-api.service";
import {
	getAdminProduct,
	listAdminBrands,
	PublishBlockedError,
	type PublishBlocker,
	updateAdminProduct,
} from "@/features/catalog/services/catalog-api.service";
import { hasPermission } from "@/lib/permissions";

/**
 * The product editor: Basics, Specifications, Variants, Media, Commercial,
 * Pricing and Provenance.
 *
 * This is where plan 008's premise gets tested -- "an operator with no
 * engineering access can create, complete and publish a product". The
 * Specifications tab is generated entirely from the category's spec template, so
 * a new category needs no code change; see `SpecAttributeField`.
 *
 * PUBLISHING IS A SEPARATE ACT from saving, and deliberately so. The backend
 * charges `admin:catalog:publish` for a status change and only `write` for an
 * edit, so an operator who may not publish still gets a working Save. Folding
 * publish into Save would hand that role a button that always fails.
 *
 * PRICING SAVES ON ITS OWN. A price is a dated observation rather than a product
 * field, so the Pricing tab records one with its own request and stays out of
 * the draft that Save changes sends and the unsaved-work guard watches.
 */

interface BasicsDraft {
	name: string;
	sku: string;
	hsnCode: string;
	tier: string;
	uom: string;
	categoryId: string;
	categoryName: string;
}

/**
 * A percent an operator typed, as the basis points the column stores.
 *
 * `null` for an empty field, which UNSETS the rate -- and unset is a real state
 * here rather than a missing one. The rate is never inherited from the category:
 * the schema is explicit that "rates are per-SKU in reality, and plywood alone
 * spans 12% and 18%", so a blank is a genuine gap and must be storable as one.
 *
 * `undefined` for anything unparseable, which leaves the stored value alone
 * rather than clearing it on a typo.
 */
export function percentToBasisPoints(
	percent: string,
): number | null | undefined {
	const trimmed = percent.trim();
	if (trimmed === "") return null;
	const value = Number(trimmed);
	if (!Number.isFinite(value) || value < 0 || value > 100) return undefined;
	return Math.round(value * 100);
}

/** The backend charges this for a status change; Save costs only `write`. */
const CATALOG_PUBLISH = "admin:catalog:publish";

/** What `POST /products/:id/duplicate` charges. Creating a product is a write. */
const CATALOG_WRITE = "admin:catalog:write";

/** What marking a price as checked against its source charges. */
const CATALOG_VERIFY = "admin:catalog:verify";

/**
 * A spec bag with its keys in a fixed order.
 *
 * `useDirtyState` compares with `JSON.stringify`, which is key-order sensitive,
 * and the spec editor DELETES a key when a field is emptied and appends it again
 * when the operator types -- correctly, because an unset value is expressed by
 * the key's absence. So clearing a value and re-typing the same one moved the key
 * to the end and left the form permanently dirty over a bag identical to the
 * loaded one, prompting on every navigation until the operator saved something
 * they had not changed. Normalising both sides is the fix; changing the editor
 * is not, and the payload keeps using the unsorted state.
 */
function sortedSpecs(
	specs: Record<string, SpecValue>,
): Record<string, SpecValue> {
	return Object.fromEntries(
		Object.entries(specs).sort(([a], [b]) => a.localeCompare(b)),
	);
}

/** Blockers that belong to a specification, keyed by attribute name. */
function specBlockers(blockers: PublishBlocker[]): Set<string> {
	return new Set(
		blockers
			.filter((blocker) => blocker.attributeName)
			.map((blocker) => blocker.attributeName as string),
	);
}

export function CatalogProductEditorPage() {
	const { productId = "" } = useParams();

	const productQuery = useQuery({
		queryKey: ["admin-catalog-product", productId],
		queryFn: () => getAdminProduct(productId),
		enabled: !!productId,
	});

	/*
	 * Bumped after a save, to re-read the form from the refreshed row.
	 *
	 * It rides in the form's `key`, so a bump remounts it and its state
	 * initialisers run again against fresh data. One mechanism replaces three: an
	 * effect that copied the query result into state, a ref remembering which
	 * product had been seeded so a refetch could not re-run it, and an explicit
	 * reseed after every save because clearing that ref reopened the window it
	 * existed to close.
	 *
	 * Only on SUCCESS. A refused publish must leave the form exactly as typed,
	 * with its blockers on screen.
	 */
	const [reseedToken, setReseedToken] = useState(0);

	/*
	 * The tab lives ABOVE the reset boundary, so a save does not throw the
	 * operator back to Basics.
	 *
	 * `<Tabs>` was uncontrolled and inside the remounted subtree, so its
	 * selection died with every successful save -- someone editing
	 * Specifications pressed Save and landed on Basics, with no indication why.
	 * The old ref-based reseed never remounted anything, so this only became
	 * possible when the reset became a remount.
	 */
	const [tab, setTab] = useState("basics");

	/*
	 * A REF, not the `productId` variable.
	 *
	 * The callback below is captured by the form that started the save, so its
	 * closure holds the id from that render -- comparing against it compares p1
	 * with p1 and always matches, which is exactly what the first version of
	 * this guard did. The ref is read at call time, so it holds whatever the
	 * operator is looking at when the save actually lands.
	 */
	const shownProductId = useRef(productId);
	shownProductId.current = productId;

	/*
	 * Nothing to show is the condition, not `isError`. React Query keeps
	 * `isError` set after a failed BACKGROUND refetch while `data` still holds
	 * the last good copy, so keying this on `isError` would replace a loaded,
	 * possibly half-edited form because a refresh failed.
	 */
	if (!productQuery.data) {
		if (productQuery.isError) {
			return (
				<div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
					Could not load this product.{" "}
					<button
						type="button"
						className="underline"
						onClick={() => productQuery.refetch()}
					>
						Try again
					</button>
				</div>
			);
		}
		return (
			<div className="grid gap-3">
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-64 w-full" />
			</div>
		);
	}

	return (
		<ProductEditorForm
			key={`${productId}:${reseedToken}`}
			productId={productId}
			data={productQuery.data}
			tab={tab}
			onTabChange={setTab}
			/*
			 * Identity-checked, because a save can outlive the screen that started
			 * it. React Query runs a `useMutation`-level `onSuccess` from the
			 * mutation cache with no mounted-observer check, and this shell is the
			 * route element for every `:productId` -- so it is NOT remounted when
			 * the operator opens a different product. Without the check, a save on
			 * p1 that resolves after the operator moved to p2 bumps the token that
			 * keys p2's form, discarding p2's unsaved edits for a save that had
			 * nothing to do with it.
			 */
			onSaved={(savedProductId) => {
				if (savedProductId !== shownProductId.current) return;
				setReseedToken((token) => token + 1);
			}}
		/>
	);
}

/** Everything here reads a product that has ALREADY loaded. */
function ProductEditorForm({
	productId,
	data,
	tab,
	onTabChange,
	onSaved,
}: {
	productId: string;
	data: AdminProductDetail;
	tab: string;
	onTabChange: (tab: string) => void;
	onSaved: (savedProductId: string) => void;
}) {
	const queryClient = useQueryClient();
	const operator = useAdminAuthStore((state) => state.operator);
	const canPublish = hasPermission(operator, CATALOG_PUBLISH);
	const canWrite = hasPermission(operator, CATALOG_WRITE);
	const canVerify = hasPermission(operator, CATALOG_VERIFY);
	/*
	 * The COMMERCIAL draft, separate from `basics` because its two fields are
	 * governed by two different permissions and neither belongs on the Basics
	 * form. Seeded once, like `basics`, for the same reason.
	 */
	/*
	 * The brand picker's options. Merged brands are included deliberately here,
	 * unlike the source screen: a product already filed under one must still
	 * render its own brand rather than showing an empty control -- and moving it
	 * OFF is exactly the correction an operator would be making.
	 */
	const brandOptions = useQuery({
		queryKey: ["admin-catalog-brands"],
		queryFn: listAdminBrands,
		// Only the Commercial tab reads it, and most edits never open that tab.
		enabled: tab === "commercial",
	});

	/** The product's own HSN, which overrides the category's. */
	const productHsn =
		(data.product as { hsnCode?: string | null }).hsnCode ?? null;

	const [commercial, setCommercial] = useState(() => ({
		brandId: data.brand?.id ?? "",
		// Basis points in the column, percent on the screen. This is the admin's
		// first basis-points control and there is no formatter to copy, so the
		// conversion is here and the label says "%".
		gstPercent:
			typeof (data.product as { gstRateBp?: number | null }).gstRateBp ===
			"number"
				? String(
						((data.product as { gstRateBp?: number | null }).gstRateBp ?? 0) /
							100,
					)
				: "",
	}));
	const navigate = useNavigate();
	const [duplicating, setDuplicating] = useState(false);

	/*
	 * Initialised ONCE. This component only exists once a product has loaded, and
	 * is remounted when a different one does, so there is nothing to keep in step
	 * and no effect to get wrong.
	 */
	const [basics, setBasics] = useState<BasicsDraft>(() => ({
		name: data.product.name ?? "",
		sku: data.product.sku ?? "",
		hsnCode: (data.product as { hsnCode?: string | null }).hsnCode ?? "",
		tier: data.product.tier ?? "standard",
		uom: data.product.uom ?? "",
		categoryId: data.category?.id ?? "",
		categoryName: data.category?.name ?? "",
	}));
	const [specs, setSpecs] = useState<Record<string, SpecValue>>(
		() => (data.product.specifications ?? {}) as Record<string, SpecValue>,
	);
	const [images, setImages] = useState<CatalogProductImage[]>(
		() => data.product.images ?? [],
	);
	/*
	 * True while the Media tab has files in flight.
	 *
	 * `images` has two asynchronous writers -- the upload's completion, and this
	 * page's own reset after a save -- and neither knows about the other. A save
	 * landing mid-upload PATCHes a list without the pending files, and the reset
	 * that follows discards the draft the upload was going to write into. That
	 * reset used to be a reseed and is now a remount, which makes the loss more
	 * total rather than less: the upload's completion setter fires against an
	 * instance React has already thrown away, and is dropped in silence.
	 *
	 * Blocking the save for the seconds an upload takes is the cheap half of
	 * that; the alternative is reconciling two writers, which is a lot of
	 * machinery for a race the operator cannot even perceive.
	 */
	const [imagesUploading, setImagesUploading] = useState(false);

	/*
	 * WHAT THIS SCREEN MEANS BY "UNSAVED", as one value the baseline can be
	 * compared against.
	 *
	 * `useDirtyState` compares with `JSON.stringify`, so what goes in decides
	 * what counts. Three deliberate inclusions and one deliberate exclusion:
	 *
	 * - `specs` and `images` are in, by VALUE. They are the two edits an operator
	 *   loses most work to, and both are objects the loaded row also holds -- so
	 *   the comparison is structural rather than by identity.
	 * - `commercial` is in. It is a tab an operator can type in and leave.
	 * - `product.updatedAt` is OUT, and its absence is the point: a refetch moves
	 *   it on every poll, and including it would report a clean form as dirty
	 *   forever. Nothing here reads it, which is how it stays out.
	 */
	const draft = { basics, specs: sortedSpecs(specs), images, commercial };

	/*
	 * The baseline is the LOADED row expressed the same way, MEMOISED ON `data`.
	 *
	 * `useDirtyState` re-seeds when the value it is handed changes IDENTITY --
	 * that is how navigating product A to product B re-baselines an editor that
	 * never unmounts. So the object has to be stable between renders and new when
	 * a different product loads, which is exactly React Query's `data`.
	 *
	 * Rebuilding it inline every render is an infinite loop, not a slow path: the
	 * hook sees a new reference, calls `setBaseline`, re-renders, sees another new
	 * reference. That is what it did first, and React stopped it with "Maximum
	 * update depth exceeded".
	 *
	 * Typed as the draft's shape so the two are compared as one type: without the
	 * annotation TypeScript narrows `tier` here to its literal union and the
	 * draft's `string` no longer matches.
	 */
	// `data` is the whole key: the fields below are read from it, and depending on
	// any of them individually would defeat the identity check above.
	const loaded: typeof draft = useMemo(
		() => ({
			basics: {
				name: data.product.name ?? "",
				sku: data.product.sku ?? "",
				hsnCode: (data.product as { hsnCode?: string | null }).hsnCode ?? "",
				tier: data.product.tier ?? "standard",
				uom: data.product.uom ?? "",
				categoryId: data.category?.id ?? "",
				categoryName: data.category?.name ?? "",
			},
			specs: sortedSpecs(
				(data.product.specifications ?? {}) as Record<string, SpecValue>,
			),
			images: data.product.images ?? [],
			commercial: {
				brandId: data.brand?.id ?? "",
				gstPercent:
					typeof (data.product as { gstRateBp?: number | null }).gstRateBp ===
					"number"
						? String(
								((data.product as { gstRateBp?: number | null }).gstRateBp ??
									0) / 100,
							)
						: "",
			},
		}),
		[data],
	);

	/*
	 * The GST rate as the payload will carry it, computed ONCE.
	 *
	 * `undefined` means unparseable or outside 0-100, and `undefined` is dropped
	 * from the JSON body, so the server leaves the stored rate alone. That is the
	 * right thing to do with a typo and only half a policy: the operator typed
	 * 150, got "Changes saved", and watched the field revert to the old value with
	 * a success message on screen. Computing it here lets the same value gate the
	 * save and fill the request, so the guard and the payload cannot diverge.
	 */
	const gstRateBp = percentToBasisPoints(commercial.gstPercent);
	const gstRejected =
		commercial.gstPercent.trim() !== "" && gstRateBp === undefined;

	// `reset` is not destructured: the remount on save re-baselines this form.
	const { isDirty } = useDirtyState(loaded);
	/*
	 * An upload in flight is unsaved work. The files are not in `images` yet and
	 * leaving now loses them, so the guard has to count it even though nothing
	 * the operator typed has changed.
	 */
	const { allowNextNavigation } = useEditorExitGuard(
		isDirty(draft) || imagesUploading,
	);
	/*
	 * Whether the CONTENT patch succeeded before a publish was refused.
	 *
	 * Blockers can arrive two ways now. Publishing a draft sends content first
	 * and status second, so a refusal means the work is safely stored. Editing an
	 * ALREADY-ACTIVE product into an incomplete state is refused on the content
	 * patch itself, and nothing is stored. The banner must not tell an operator
	 * their work is saved in the second case.
	 */
	const contentSaved = useRef(false);
	const [blockers, setBlockers] = useState<PublishBlocker[]>([]);
	/** The category the loaded product actually has, to detect a change. */
	const savedCategoryId = data.category?.id ?? "";

	const save = useMutation({
		mutationFn: async (input: { publish?: boolean }) => {
			// Not a silent no-op. A mutation that resolves without calling the API
			// fires onSuccess and tells the operator "Changes saved" when nothing
			// was sent -- the one outcome worse than an error.
			if (!basics) throw new Error("The product has not finished loading.");
			contentSaved.current = false;

			/*
			 * Specifications are CLEARED when the category changed -- sent as `{}`,
			 * not omitted.
			 *
			 * The spec template hangs off the category, so the values on screen
			 * belong to the OLD category's template. Sending them with the new
			 * category writes one category's answers onto another's product, where
			 * they are neither shown nor removable.
			 *
			 * Omitting the key does NOT achieve that. `updateProduct` does
			 * `.set({ ...data })`, so a key that is absent from the patch leaves the
			 * column exactly as it was: the old answers stayed on the row, the reseed
			 * loaded them straight back into `specs`, and the NEXT save re-sent them
			 * under the new category -- where `validateSpecifications` waves through
			 * any attribute the template does not know about. The toast below said
			 * they had been cleared while all of that was happening.
			 */
			const categoryChanged = basics.categoryId !== savedCategoryId;

			await updateAdminProduct(productId, {
				name: basics.name,
				sku: basics.sku,
				// Empty means "no product-level code" -- null, so the server unsets it
				// and the category's own HSN takes over again, rather than storing an
				// empty string the gate would treat as present.
				hsnCode: basics.hsnCode.trim() === "" ? null : basics.hsnCode.trim(),
				tier: basics.tier,
				uom: basics.uom,
				categoryId: basics.categoryId,
				specifications: categoryChanged ? {} : specs,
				/*
				 * Sent only when this client actually CHANGED the list.
				 *
				 * It has to be sent when the operator edited it, because
				 * `updateProductSchema` replaces the array rather than merging --
				 * omitting it after a removal would quietly restore the deleted image.
				 *
				 * But sending it unconditionally is a whole-column overwrite with no
				 * version check, so a second tab open on the same product destroys
				 * what the first one added: tab 1 uploads an image and saves, tab 2
				 * fixes a typo and saves the list it loaded ten minutes ago, and the
				 * image is gone along with the file it pointed at. Sending it only
				 * when it differs from what this client loaded keeps the removal case
				 * and drops the clobber.
				 *
				 * This is not real concurrency control -- two tabs that BOTH edit
				 * images still race, and fixing that needs an `updatedAt` precondition
				 * the API does not have. It removes the case where an untouched tab
				 * destroys someone else's work.
				 */
				...(sameImages(images, data.product.images ?? []) ? {} : { images }),
				/*
				 * The brand is sent only when the operator could edit it. Sending it
				 * from a read-only control would charge `admin:catalog:publish` on
				 * every save an operator without it makes -- the route charges on the
				 * field's PRESENCE, not on whether its value changed.
				 */
				...(canPublish ? { brandId: commercial.brandId || null } : {}),
				gstRateBp,
			});

			/*
			 * PUBLISHING IS A SECOND REQUEST, and that is the whole point.
			 *
			 * `updateProduct` runs the publish gate BEFORE it writes, so a refused
			 * publish persisted nothing at all -- an operator who filled in five
			 * specifications, entered an HSN and uploaded an image lost every one of
			 * them to a blocker about something else entirely. The work was gone and
			 * the banner explained why the work they could no longer see had been
			 * rejected.
			 *
			 * Splitting it means the content lands first and survives whatever the
			 * gate decides. The gate does not fire on the content patch itself while
			 * the product is a draft, so the ordinary case costs one extra round
			 * trip and nothing else. If the second request is refused, the blockers
			 * still come back and the banner still names them -- against a product
			 * that now actually holds the work.
			 */
			contentSaved.current = true;

			if (input.publish) {
				await updateAdminProduct(productId, { status: "active" });
			}

			return { categoryChanged };
		},
		onSuccess: async (result, input) => {
			setBlockers([]);
			/*
			 * NO EXPLICIT BASELINE RESET, and its absence is deliberate.
			 *
			 * A successful save bumps `reseedToken`, which is part of this form's
			 * `key`, so the whole component remounts and `useDirtyState` rebuilds
			 * its baseline from the loaded row. An explicit `reset` here survived
			 * its mutant for exactly that reason -- there was no state left for it
			 * to correct.
			 */
			await queryClient.invalidateQueries({
				queryKey: ["admin-catalog-product", productId],
			});
			await queryClient.invalidateQueries({
				queryKey: ["admin-catalog-products"],
			});

			// Remount against the refreshed row, which is what picks up anything
			// the server normalised on the way in. Named, so the shell can tell a
			// save for THIS product from one the operator has since navigated away
			// from.
			onSaved(productId);

			notify.done(input.publish ? "Product published" : "Changes saved");
			if (result?.categoryChanged) {
				notify.partly(
					"Category changed, so specifications were cleared. Fill in the new category's specifications below.",
				);
			}
		},
		onError: async (error) => {
			/*
			 * The banner, and NO toast.
			 *
			 * `apps/admin/src/lib/api-client.ts` already toasts `data.message` for
			 * every non-401/429 failure, including this one -- so an earlier version
			 * of this handler, which toasted the same message again, produced two
			 * identical toasts per failed save. On a 401 whose refresh also failed it
			 * produced two DIFFERENT ones, the second describing the refresh rather
			 * than the save the operator had just attempted.
			 *
			 * What the interceptor cannot do is set this page's blocker state, so
			 * that is all this handler does. The gate answers with EVERY blocker at
			 * once; showing one and hiding the rest is what turns a ten-minute
			 * product into a forty-minute one.
			 */
			if (error instanceof PublishBlockedError) {
				setBlockers(error.blockers);

				/*
				 * Refreshed, but NOT toasted. The api client already toasts the
				 * server's sentence for this failure, and a second toast saying the
				 * save worked is the double-notification this page had to have
				 * removed once already. The banner below says it instead, where the
				 * blockers are.
				 *
				 * The refresh matters on its own: without it the list still shows the
				 * old row, and an operator who reloads finds work the screen implied
				 * was lost.
				 */
				if (contentSaved.current) {
					await queryClient.invalidateQueries({
						queryKey: ["admin-catalog-product", productId],
					});
					await queryClient.invalidateQueries({
						queryKey: ["admin-catalog-products"],
					});
				}
			}
		},
	});

	const { product, specTemplate } = data;
	const attributes = [...(specTemplate?.attributes ?? [])].sort(
		(a, b) => a.displayOrder - b.displayOrder,
	);
	const invalidSpecs = specBlockers(blockers);
	const nonSpecBlockers = blockers.filter((blocker) => !blocker.attributeName);

	return (
		<div className="grid gap-5">
			<div className="grid gap-2">
				<Link
					to="/catalog/products"
					className="text-xs text-muted-foreground hover:underline"
				>
					← All products
				</Link>
				<div className="flex flex-wrap items-center gap-3">
					<h2 className="text-2xl font-semibold tracking-tight">
						{product.name}
					</h2>
					<StatusBadge status={product.status} />
				</div>
			</div>

			{blockers.length > 0 ? (
				<div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
					<p className="text-sm font-semibold text-amber-900">
						This product cannot be published yet
					</p>
					{contentSaved.current ? (
						// The reassurance an operator needs first. Before publishing was
						// split from saving, a refused publish threw away everything they
						// had just entered -- so the honest thing to say now is that it
						// did not.
						<p className="mt-1 text-sm text-amber-900">
							Your changes have been saved. Only publishing is blocked.
						</p>
					) : null}
					<ul className="mt-2 grid gap-1 text-sm text-amber-900">
						{blockers.map((blocker, index) => (
							/*
							 * Keyed on POSITION. Both `field` and `message` derive from the
							 * attribute name, so two blockers about identically-named
							 * attributes collide on either -- and a duplicate key silently
							 * drops one, which is exactly the "show them all" property this
							 * banner exists for. The list is replaced wholesale on every
							 * response and never reordered, so the index is stable for as
							 * long as it needs to be.
							 */
							// biome-ignore lint/suspicious/noArrayIndexKey: see above
							<li key={index}>• {blocker.message}</li>
						))}
					</ul>
				</div>
			) : null}

			<Tabs value={tab} onValueChange={onTabChange}>
				<TabsList>
					<TabsTrigger value="basics">Basics</TabsTrigger>
					<TabsTrigger value="specifications">Specifications</TabsTrigger>
					<TabsTrigger value="variants">Variants</TabsTrigger>
					<TabsTrigger value="media">Media</TabsTrigger>
					<TabsTrigger value="commercial">Commercial</TabsTrigger>
					<TabsTrigger value="pricing">Pricing</TabsTrigger>
					<TabsTrigger value="provenance">Provenance</TabsTrigger>
				</TabsList>

				<TabsContent value="basics">
					<Card className="border-slate-200 shadow-none">
						<CardHeader>
							<CardTitle>Basics</CardTitle>
							<CardDescription>
								Name, identity and the category that decides which
								specifications this product needs.
							</CardDescription>
						</CardHeader>
						<CardContent className="grid gap-4 md:grid-cols-2">
							<div className="grid gap-1.5">
								<Label htmlFor="name">Name</Label>
								<Input
									id="name"
									disabled={save.isPending}
									value={basics.name}
									onChange={(event) =>
										setBasics({ ...basics, name: event.target.value })
									}
								/>
							</div>

							<div className="grid gap-1.5">
								<Label htmlFor="sku">SKU</Label>
								<Input
									id="sku"
									disabled={save.isPending}
									value={basics.sku}
									onChange={(event) =>
										setBasics({ ...basics, sku: event.target.value })
									}
								/>
							</div>

							<div className="grid gap-1.5">
								<Label htmlFor="category">Category</Label>
								{/*
								 * Changing this changes which specifications apply, since the
								 * template hangs off the category. The Specifications tab
								 * re-renders from the saved product, so the new template
								 * appears after saving rather than guessing at it here.
								 */}
								<CategoryPicker
									id="category"
									disabled={save.isPending}
									value={basics.categoryId || null}
									valueLabel={basics.categoryName || null}
									// A product must have a category, so there is no state where
									// clearing it helps: it only produces a 400 on save.
									clearable={false}
									onChange={(category) =>
										setBasics({
											...basics,
											categoryId: category?.id ?? "",
											categoryName: category?.name ?? "",
										})
									}
								/>
							</div>

							<div className="grid gap-1.5">
								<Label htmlFor="hsnCode">HSN code</Label>
								<Input
									id="hsnCode"
									inputMode="numeric"
									maxLength={8}
									placeholder="Inherited from the category"
									disabled={save.isPending}
									value={basics.hsnCode}
									onChange={(event) =>
										setBasics({ ...basics, hsnCode: event.target.value })
									}
								/>
								{/*
								 * Publishing needs an HSN, and only 306 of the 674 taxonomy
								 * categories carry one -- so for a product in any of the other
								 * 368 this field is the difference between publishable and
								 * permanently blocked by a message naming a code the operator
								 * had nowhere to enter.
								 */}
								<p className="text-xs text-muted-foreground">
									4, 6 or 8 digits. Leave blank to use the category's code.
								</p>
							</div>

							<div className="grid gap-1.5">
								<Label htmlFor="uom">Unit of measure</Label>
								{/*
								 * A closed list, not free text. `uom` is a Postgres enum and a
								 * zod enum; anything typed that is not one of these twenty
								 * values is a 400 the operator had no way to predict, and
								 * "sqft" versus "sq_ft" is the obvious way to hit it.
								 */}
								<Select
									value={basics.uom}
									onValueChange={(uom) => setBasics({ ...basics, uom })}
									disabled={save.isPending}
								>
									<SelectTrigger id="uom">
										<SelectValue placeholder="Select a unit" />
									</SelectTrigger>
									<SelectContent>
										<SelectGroup>
											{PRODUCT_UOMS.map((uom) => (
												<SelectItem key={uom} value={uom}>
													{uomLabel(uom)}
												</SelectItem>
											))}
										</SelectGroup>
									</SelectContent>
								</Select>
							</div>

							<div className="grid gap-1.5">
								<Label htmlFor="tier">Tier</Label>
								<Select
									value={basics.tier}
									onValueChange={(tier) => setBasics({ ...basics, tier })}
									disabled={save.isPending}
								>
									<SelectTrigger id="tier" aria-label="Tier">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectGroup>
											{PRODUCT_TIERS.map((tier) => (
												<SelectItem key={tier} value={tier}>
													{tier}
												</SelectItem>
											))}
										</SelectGroup>
									</SelectContent>
								</Select>
							</div>
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value="specifications">
					<Card className="border-slate-200 shadow-none">
						<CardHeader>
							<CardTitle>Specifications</CardTitle>
							<CardDescription>
								{specTemplate
									? "Generated from this category's spec template. Required attributes must be filled before publishing."
									: "This category has no spec template, so there are no specifications to fill."}
							</CardDescription>
						</CardHeader>
						<CardContent className="grid gap-4 md:grid-cols-2">
							{attributes.length === 0 ? (
								<p className="text-sm text-muted-foreground">
									Nothing to fill in. A template can be added to this category
									from the taxonomy screens.
								</p>
							) : (
								attributes.map((attribute) => (
									<SpecAttributeField
										key={attribute.id}
										attribute={attribute}
										/*
										 * Read-only while a save is in flight. The buttons were
										 * already disabled, but the FIELDS were not -- so an
										 * operator could keep typing into a form whose contents
										 * were about to be replaced by the server's copy.
										 */
										disabled={save.isPending}
										value={specs[attribute.name]}
										invalid={invalidSpecs.has(attribute.name)}
										onChange={(next) => {
											setSpecs((previous) => {
												const draft = { ...previous };
												if (next === undefined) delete draft[attribute.name];
												else draft[attribute.name] = next;
												return draft;
											});
											// Stop flagging an attribute the operator has just
											// answered. Leaving the mark until the next Save meant
											// a form that stayed red while being fixed, which reads
											// as "that did not work".
											setBlockers((previous) =>
												previous.filter(
													(blocker) => blocker.attributeName !== attribute.name,
												),
											);
										}}
									/>
								))
							)}
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value="media">
					<Card className="border-slate-200 shadow-none">
						<CardHeader>
							<CardTitle>Media</CardTitle>
							<CardDescription>
								At least one image is required before this product can be
								published, and a product with none is effectively invisible in
								browse.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<ProductMedia
								productId={productId}
								images={images}
								onUploadingChange={setImagesUploading}
								disabled={save.isPending}
								invalid={blockers.some((blocker) => blocker.field === "images")}
								onChange={(next) => {
									setImages(next);
									// Stop flagging images the moment one is added, rather than
									// leaving the banner red while the operator fixes it.
									if (next.length > 0) {
										setBlockers((previous) =>
											previous.filter((blocker) => blocker.field !== "images"),
										);
									}
								}}
							/>
						</CardContent>
					</Card>
				</TabsContent>
				<TabsContent value="variants">
					<Card className="border-slate-200 shadow-none">
						<CardHeader>
							<CardTitle>Variants</CardTitle>
							<CardDescription>
								The same product in a different finish, size or colour. The
								fields come from the axes this product's category declares, so a
								new category needs no code change here.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<VariantList
								productId={productId}
								variants={data.variants ?? []}
								/*
								 * The category's DECLARED axes, from its spec template. An
								 * absent template is no axes rather than a crash: a category
								 * without one is an ordinary state, and the list explains it.
								 */
								axes={data.specTemplate?.variantAttributes ?? []}
								canWrite={canWrite}
							/>
						</CardContent>
					</Card>
				</TabsContent>
				<TabsContent value="commercial">
					<Card className="border-slate-200 shadow-none">
						<CardHeader>
							<CardTitle>Commercial</CardTitle>
							<CardDescription>
								Who makes this product, and what tax it attracts. {/*
								 * Said, not left to be discovered. A tab named Commercial
								 * showing two fields reads as broken, and the two an
								 * operator looks for here are edited elsewhere.
								 */}
								Its tier is on Basics, and whether it is live is the publish
								action beneath these tabs.
							</CardDescription>
						</CardHeader>
						<CardContent className="grid gap-4 md:grid-cols-2">
							<div className="grid gap-1.5">
								<Label htmlFor="commercial-brand">Brand</Label>
								<Select
									value={commercial.brandId}
									disabled={!canPublish}
									onValueChange={(value) =>
										setCommercial((current) => ({
											...current,
											brandId: value,
										}))
									}
								>
									<SelectTrigger id="commercial-brand">
										<SelectValue placeholder="No brand" />
									</SelectTrigger>
									<SelectContent>
										<SelectGroup>
											{(brandOptions.data ?? []).map((brand) => (
												<SelectItem key={brand.id} value={brand.id}>
													{brand.name}
												</SelectItem>
											))}
										</SelectGroup>
									</SelectContent>
								</Select>
								{canPublish ? (
									<p className="text-xs text-muted-foreground">
										Moving a product to a withdrawn brand takes it out of
										search, browse and every BOQ, which is why this costs the
										publish permission.
									</p>
								) : (
									/*
									 * NAMED, not merely disabled. `brandId` is charged at
									 * `admin:catalog:publish` in the route's field-permission
									 * map, and the seeded `catalog_operator` holds write
									 * WITHOUT publish -- so this is the control that role meets
									 * every day. A disabled control with no stated reason is
									 * the one they click twice and report as broken.
									 */
									<p className="text-xs text-muted-foreground">
										Changing the brand needs the publish permission, because
										re-pointing a product at a withdrawn brand takes it out of
										search and every BOQ.
									</p>
								)}
							</div>

							<div className="grid gap-1.5">
								<Label htmlFor="commercial-gst">GST rate (%)</Label>
								<Input
									id="commercial-gst"
									type="number"
									min={0}
									max={100}
									step="0.01"
									disabled={!canWrite}
									value={commercial.gstPercent}
									onChange={(event) =>
										setCommercial((current) => ({
											...current,
											gstPercent: event.target.value,
										}))
									}
								/>
								{gstRejected ? (
									<p role="alert" className="text-xs text-red-700">
										Enter a percentage between 0 and 100. Leave it blank to
										record no rate.
									</p>
								) : null}
								<p className="text-xs text-muted-foreground">
									A per-product record for tax work, kept because rates are per
									SKU in reality — plywood alone spans 12% and 18%. It is never
									inherited from the category, so a blank here is a gap rather
									than a value borrowed from above.
								</p>
								<p className="text-xs text-muted-foreground">
									It does not change what a customer is quoted: tax on a price
									comes from this product&apos;s HSN code.
								</p>
							</div>

							<div className="grid gap-1.5 md:col-span-2">
								<p className="text-sm font-medium">Classification</p>
								<p className="text-sm text-muted-foreground">
									{productHsn ? (
										<>
											This product carries HSN{" "}
											<span className="font-medium">{productHsn}</span>, set on
											Basics.
										</>
									) : data.category?.hsnCode ? (
										<>
											This product has no HSN of its own and inherits{" "}
											<span className="font-medium">
												{data.category.hsnCode}
											</span>{" "}
											from {data.category.name}.
										</>
									) : (
										<>
											Neither this product nor{" "}
											{data.category?.name ?? "its category"} carries an HSN
											code. One is needed before it can be published.
										</>
									)}
								</p>
							</div>
						</CardContent>
					</Card>
				</TabsContent>
				<TabsContent value="pricing">
					<Card className="border-slate-200 shadow-none">
						<CardHeader>
							<CardTitle>Pricing</CardTitle>
							<CardDescription>
								Every price is a dated observation from a source. Customers see
								the one the catalogue picks for each variant, which is not
								necessarily the latest one entered. Prices are saved here, not
								by Save changes.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<ProductPricing
								productId={productId}
								uom={product.uom}
								variants={data.variants ?? []}
								price={data.price}
								prices={data.prices}
								productStatus={product.status}
								canWrite={canWrite}
								canVerify={canVerify}
							/>
						</CardContent>
					</Card>
				</TabsContent>
				<TabsContent value="provenance">
					<Card className="border-slate-200 shadow-none">
						<CardHeader>
							<CardTitle>Provenance</CardTitle>
							<CardDescription>
								Who changed this product, when, and what the value was before.
								Read-only: reverting is an ordinary edit, and should be recorded
								as one.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<ProductProvenance productId={productId} />
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>

			<div className="flex flex-wrap items-center gap-3 border-t pt-4">
				<Button
					onClick={() => save.mutate({})}
					disabled={save.isPending || imagesUploading || gstRejected}
				>
					{save.isPending ? "Saving…" : "Save changes"}
				</Button>

				{/*
				 * Publish is hidden without `admin:catalog:publish`, not merely
				 * disabled-on-click. The permission split is the reason this button is
				 * separate from Save at all: a data-entry operator gets a working Save,
				 * and showing them a Publish that always 403s undoes the whole point.
				 */}
				{product.status !== "active" && canPublish ? (
					<Button
						variant="outline"
						onClick={() => save.mutate({ publish: true })}
						disabled={save.isPending || imagesUploading || gstRejected}
					>
						Save and publish
					</Button>
				) : null}

				{/*
				 * Hidden without `admin:catalog:write`, for the same reason Publish is
				 * hidden without `admin:catalog:publish`: the route charges it, so
				 * showing it to an operator who lacks it offers a button that always
				 * 403s.
				 *
				 * Sits beside Save rather than in the tabs because duplicating is
				 * something done TO this product, not an edit to part of it.
				 */}
				{canWrite ? (
					<Button
						variant="outline"
						onClick={() => setDuplicating(true)}
						disabled={save.isPending || imagesUploading || gstRejected}
					>
						Duplicate
					</Button>
				) : null}

				{imagesUploading ? (
					// Said, not just enforced. A Save button that is disabled for no
					// visible reason is the control an operator clicks twice and then
					// reports as broken.
					<span className="text-xs text-muted-foreground">
						Waiting for images to finish uploading…
					</span>
				) : null}

				{nonSpecBlockers.length > 0 ? (
					<span className="text-xs text-amber-800">
						{nonSpecBlockers.length} blocker
						{nonSpecBlockers.length === 1 ? "" : "s"} outside this form — see
						above.
					</span>
				) : null}

				{attributes.some(
					(attribute) =>
						attribute.isRequired && !isFilled(specs[attribute.name]),
				) ? (
					<span className="text-xs text-muted-foreground">
						Some required specifications are still empty.
					</span>
				) : null}
			</div>

			{duplicating ? (
				<DuplicateProductDialog
					productId={productId}
					productName={product.name}
					onClose={() => setDuplicating(false)}
					onDuplicated={(newProductId) => {
						setDuplicating(false);
						/*
						 * Straight to the copy. A duplicate an operator has to go and
						 * find is a duplicate they cannot tell succeeded -- and the
						 * next thing they do is always edit it, because the whole point
						 * was to change one or two fields.
						 *
						 * The list is invalidated as well: this editor is reached from
						 * it, and a copy missing from that list on the way back reads as
						 * a failure.
						 */
						queryClient.invalidateQueries({
							queryKey: ["admin-catalog-products"],
						});
						/*
						 * The editor may still be dirty -- duplicating does not save
						 * this product -- and the guard would otherwise prompt about
						 * THIS product's unsaved edits while sending the operator to a
						 * different one. Cancel would then leave the copy created and
						 * unreached, and a second Duplicate makes a third product.
						 */
						allowNextNavigation();
						navigate(`/catalog/products/${newProductId}`);
					}}
				/>
			) : null}
		</div>
	);
}
